import { Component, EventEmitter, forwardRef, Input, Output, ViewChild, ElementRef, ChangeDetectorRef, HostBinding } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validator, AbstractControl, ValidationErrors, NG_VALIDATORS } from '@angular/forms';
import { Injector } from '@angular/core';
import { autoId } from 'projects/borrower-app/src/app/services/utils';

export interface AddressSelectedEvent {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
  businessName?: string;
  country?: string;
}

@Component({
    selector: 'address-autocomplete-v2',
    templateUrl: './address-autocomplete-v2.component.html',
    styleUrls: ['./address-autocomplete-v2.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AddressAutocompleteV2Component),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => AddressAutocompleteV2Component),
            multi: true
        }
    ],
    standalone: false
})
export class AddressAutocompleteV2Component implements ControlValueAccessor, Validator {

  constructor(
    private cdr: ChangeDetectorRef,
    private injector: Injector
  ) {}

  get ngControl(): NgControl | null {
    return this.injector.get(NgControl, null);
  }

  @ViewChild('addressInput', { static: true })
  addressInput!: ElementRef<HTMLInputElement>;

  @ViewChild('dropdown', { static: false })
  dropdown!: ElementRef<HTMLUListElement>;

  @Output()
  addressSelected = new EventEmitter<AddressSelectedEvent>();

  @Output()
  setManuallyEnteredAddress = new EventEmitter<string>();

  @Input('id') inputId?: string;
  @HostBinding('attr.id') id: string = autoId();

  @Input('name') inputName?: string;
  @HostBinding('attr.name') hostName: string = autoId();

  @Input()
  disabled: boolean = false;

  @Input()
  includedPrimaryTypes: string[] = ['street_address'];

  @Input()
  autocompleteDisabled?: boolean;

  @Input()
  placeholder: string = 'Enter address';

  @Input()
  required?: boolean;

  // ControlValueAccessor
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string): void {
    this.inputValue = value || '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator
  private onValidatorChange?: () => void;

  validate(control: AbstractControl<string>): ValidationErrors | null {
    if (this.required && (!this.inputValue || this.inputValue.trim().length === 0)) {
      return { required: true };
    }
    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  inputValue = '';
  predictions: google.maps.places.AutocompletePrediction[] = [];
  loading = false;
  errorMessage: string | null = null;
  showDropdown = false;
  debounceTimeout: any;

  private get mapsGoogle() {
    return (window as Window & { google?: typeof google }).google;
  }

  ngAfterViewInit() {
    if (!this.mapsGoogle?.maps?.places) {
      this.errorMessage = 'Google Maps Places API is not loaded.';
      this.cdr.detectChanges();
      return;
    }
    this.cdr.detectChanges();
  }

  onInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.inputValue = value;
    this.errorMessage = null;
    this.onChange(this.inputValue);
    if (this.autocompleteDisabled) {
      return;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    if (!value.trim()) {
      this.predictions = [];
      this.showDropdown = false;
      return;
    }
    this.loading = true;
    this.debounceTimeout = setTimeout(() => {
      this.fetchPredictions(value);
    }, 500);
  }

  async fetchPredictions(input: string) {
    if (!(this.mapsGoogle?.maps?.places && (this.mapsGoogle.maps.places as any).AutocompleteSuggestion)) {
      this.loading = false;
      this.errorMessage = 'Google Maps Places API is not loaded.';
      this.predictions = [];
      this.showDropdown = false;
      this.cdr.detectChanges();
      return;
    }
    try {
      const result = await (this.mapsGoogle.maps.places as any).AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedPrimaryTypes: this.includedPrimaryTypes,
        includedRegionCodes: ['US']
      });
      this.loading = false;
      if (!result || !result.suggestions || result.suggestions.length === 0) {
        this.predictions = [];
        this.showDropdown = false;
        this.errorMessage = 'No address suggestions found.';
        this.cdr.detectChanges();
        return;
      }
      this.predictions = result.suggestions.map(suggestion => {
        return {
          place_id: suggestion.placePrediction.placeId,
          structured_formatting: {
            main_text: suggestion.placePrediction.text.text,
            secondary_text: suggestion.placePrediction.secondaryText?.text || ''
          }
        } as google.maps.places.AutocompletePrediction;
      });
      this.showDropdown = true;
      this.cdr.detectChanges();
    } catch (e) {
      this.loading = false;
      this.predictions = [];
      this.showDropdown = false;
      this.errorMessage = 'Failed to fetch address suggestions.';
      this.cdr.detectChanges();
    }
  }

  async onSelectPrediction(prediction: google.maps.places.AutocompletePrediction) {
    if (!(this.mapsGoogle?.maps?.places && (this.mapsGoogle.maps.places as any).Place)) {
      this.errorMessage = 'Google Maps Places API is not loaded.';
      return;
    }
    this.loading = true;
    this.showDropdown = false;
    this.inputValue = prediction.structured_formatting.main_text;
    this.predictions = [];
    this.onChange(this.inputValue);
    try {
      const place = new (this.mapsGoogle.maps.places as any).Place({ id: prediction.place_id });

      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'displayName']
      });
      this.loading = false;

      if (!place) {
        this.errorMessage = 'Failed to retrieve address details.';
        return;
      }

      let parsed: AddressSelectedEvent;

      if (place.addressComponents && place.addressComponents.length > 0) {
        parsed = this.parseAddress(place.addressComponents);
      } else {
        const displayAddress = place.displayName || place.formattedAddress || prediction.structured_formatting.main_text;
        parsed = {
          streetAddress: displayAddress,
          city: '',
          state: '',
          zipCode: '',
          county: '',
          country: 'us',
          businessName: (this.includedPrimaryTypes || []).some(t => t === 'establishment' || t === 'point_of_interest') ? (place.displayName || '') : undefined
        };

        if (place.formattedAddress) {
          const addressParts = place.formattedAddress.split(',');
          if (addressParts.length >= 3) {
            parsed.city = addressParts[1]?.trim() || '';
            const stateZipPart = addressParts[2]?.trim() || '';
            const stateZipMatch = stateZipPart.match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/);
            if (stateZipMatch) {
              parsed.state = stateZipMatch[1] || '';
              parsed.zipCode = stateZipMatch[2] || '';
            }
          }
        }
      }

      if (!parsed.streetAddress && (place.displayName || place.formattedAddress)) {
        parsed.streetAddress = place.displayName || place.formattedAddress;
      }

      const isEstablishment = (this.includedPrimaryTypes || []).some(t => t === 'establishment' || t === 'point_of_interest');
      if (isEstablishment) {
        const businessName = place.displayName || '';
        if (businessName && (!parsed.businessName || parsed.businessName === '')) {
          parsed.businessName = businessName;
        }
      }

      this.inputValue = isEstablishment ? parsed.businessName || prediction.structured_formatting.main_text : parsed.streetAddress || prediction.structured_formatting.main_text;
      this.onChange(this.inputValue);
      this.addressSelected.emit(parsed);
    } catch (e) {
      this.loading = false;
      this.errorMessage = 'Failed to retrieve address details.';
    }
  }

  onClear() {
    this.inputValue = '';
    this.predictions = [];
    this.showDropdown = false;
    this.errorMessage = null;
    this.onChange(this.inputValue);
    this.addressSelected.emit({
      streetAddress: '', city: '', state: '', zipCode: '', county: ''
    });
  }

  onBlur() {
    this.onTouched();
    setTimeout(() => {
      this.showDropdown = false;
    }, 200);
    this.setManuallyEnteredAddress.emit(this.inputValue);
  }

  focus(options?: FocusOptions): void {
    this.addressInput.nativeElement.focus(options);
  }

  parseAddress(components: any[]): AddressSelectedEvent {
    let streetNumber = '', route = '', city = '', state = '', zipCode = '', county = '', businessName = '';
    for (const comp of components) {
      if (!comp.types || !Array.isArray(comp.types)) {
        continue; // Skip if no types array
      }

      // Get the text values - from the JSON we can see longText and shortText are directly the text values
      const longName = typeof comp.longText === 'string' ? comp.longText : '';
      const shortName = typeof comp.shortText === 'string' ? comp.shortText : '';

      if (comp.types.includes('street_number')) {
        streetNumber = longName;
      } else if (comp.types.includes('route')) {
        route = longName;
      } else if (comp.types.includes('locality')) {
        city = longName;
      } else if (comp.types.includes('administrative_area_level_1')) {
        state = shortName; // Using shortName for state (e.g., 'CA' instead of 'California')
      } else if (comp.types.includes('postal_code')) {
        zipCode = longName;
      } else if (comp.types.includes('administrative_area_level_2')) {
        // County typically has 'County' suffix which we might want to remove
        county = longName.replace(/\s+County$/i, '');
      } else if (comp.types.includes('point_of_interest') || comp.types.includes('establishment')) {
        // This could be a business name
        businessName = longName;
      }
    }

    // Construct the full street address
    const streetAddress = `${streetNumber} ${route}`.trim();

    const result = {
      streetAddress,
      city,
      state,
      zipCode,
      county,
      businessName
    };

    return result;
  }
}

